"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { FileCheck2, QrCode, ScanLine, ArrowRight } from "lucide-react";

/**
 * AquaGlobe how-it-works — centered title with a teal->lavender gradient
 * underline, then 3 large horizontal frosted-glass step cards: numbered teal
 * circle + glowing glass icon tile + copy + "Learn More". Glass-native.
 */
export default function HowItWorksSteps() {
  const { t } = useTranslations();

  const steps = [
    {
      n: 1,
      Icon: FileCheck2,
      title: t("aqua.how.s1.title", "Choose a Plan"),
      body: t("aqua.how.s1.body", "Pick the data plan that fits your destination and travel needs."),
    },
    {
      n: 2,
      Icon: QrCode,
      title: t("aqua.how.s2.title", "Receive QR Code"),
      body: t("aqua.how.s2.body", "Get your eSIM QR code instantly via email after a secure checkout."),
    },
    {
      n: 3,
      Icon: ScanLine,
      title: t("aqua.how.s3.title", "Scan & Connect"),
      body: t("aqua.how.s3.body", "Scan the QR code, activate your eSIM, and stay connected."),
    },
  ];

  return (
    <section className="container mx-auto px-4 py-12">
      <Reveal as="div" className="text-center mb-10" y={16}>
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground inline-block">
          {t("aqua.how.title", "How it Works")}
        </h2>
        <span className="aqua-gradient mx-auto mt-3 block h-1.5 w-24 rounded-full" aria-hidden="true" />
      </Reveal>

      <Stagger className="space-y-5" staggerChildren={0.1} amount={0.2}>
        {steps.map(({ n, Icon, title, body }) => (
          <StaggerItem
            as="div"
            y={16}
            key={n}
            className="aqua-glass rounded-3xl p-6 backdrop-blur flex flex-col sm:flex-row sm:items-center gap-5 transition-transform hover:-translate-y-0.5"
          >
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold shrink-0 shadow-lg shadow-primary/25">
              {n}
            </span>
            <span className="aqua-icon inline-flex items-center justify-center rounded-2xl p-3 backdrop-blur shrink-0">
              <Icon className="h-6 w-6 text-primary" />
            </span>
            <div className="flex-1">
              <div className="font-semibold text-lg text-foreground">{title}</div>
              <p className="mt-1 text-sm text-muted-foreground">{body}</p>
            </div>
            <Link
              href="/how-it-works/"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline shrink-0"
            >
              {t("aqua.how.learnMore", "Learn More")} <ArrowRight className="h-4 w-4" />
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}
